<template>
  <bottom-drawer-wrapper :tabTitle="tableTabs.length <= 1 ? currentLayerName : null">
    <template #append-title v-if="tableTabs.length <= 1">
      <span class="text-caption ml-1 mr-2" style="font-size: 11px;">
        {{ loadedItemsText }}
      </span>
    </template>
    <template #navbar-content>
      <table-buttons :buttons="buttons" v-on="getTableButtonsListeners()">
        <template #billingWaterUsageAnalysis="button">
          <base-button-subheader
            :translation-path="`button.${button.name}Button`"
            small
            elevation="0"
            v-bind="button"
            color="primary"
            @click="billingWaterUsageAnalysisAction"
          ></base-button-subheader>
        </template>
        <template #selection="data">
          <v-badge
            :offset-y="20"
            :offset-x="15"
            bordered
            color="primary"
            :content="selectionBadgeNumber"
            overlap
            :value="isAnyFeatureSelected"
          >
            <v-btn v-if="isSelectionActive" v-on="data.onTooltip" icon @click="toogleOffSelection">
              <v-icon class="selection--active background">
                $selectionActive
              </v-icon>
            </v-btn>
            <dots-menu
              v-else
              :items="selectionMenuItems"
              close-on-content-click
              @single="toggleSelectionByType('single')"
              @multiple="toggleSelectionByType('multiple')"
              @freehand="toggleSelectionByType('freehand')"
              @polygon="toggleSelectionByType('polygon')"
              @radius="toggleSelectionByType('radius')"
              @sqlQuery="openSqlQueryDialog"
            >
              <template #activator="{ on }">
                <v-btn
                  icon
                  v-on="{ ...on, ...data.onTooltip }"
                  :disabled="isActiveToolNotReplaceable && !isActiveModuleIdentification"
                >
                  <v-icon>
                    $selection
                  </v-icon>
                </v-btn>
              </template>
            </dots-menu>
          </v-badge>
        </template>
        <template #realtimeStationsMeasurements="button">
          <base-button-subheader class="pr-2" v-bind="button" @click="realtimeStationsMeasurementsAction" />
        </template>
        <template #realtimeStationsDirections="button">
          <base-button-subheader v-bind="button" @click="realtimeStationsDirectionsAction" />
        </template>
      </table-buttons>
      <template v-if="!unchangedLayers">
        <slot
          name="additionalTools"
          v-bind="{ ...$data, isAnyFeatureSelected, isTasksVisible, currentLayerSelectedFeatures }"
        >
        </slot>
      </template>
      <v-col cols="auto" v-if="isEditButtonsVisible">
        <table-buttons-edit
          ref="editButtons"
          :is-any-feature-selected="isAnyFeatureSelected"
          :selected-features="currentLayerSelectedFeatures"
          :datasource="currentLayerDataSourceName"
          :geometry-type="currentLayer.geometry_type"
          @refreshTableData="refreshTableData"
          :layer-scope="currentLayer.layer_scope"
          :layer-id="layerId"
        ></table-buttons-edit>
      </v-col>
      <v-spacer></v-spacer>
      <v-col cols="auto" style="max-height: 36px; width: 180px" v-if="!unchangedLayers">
        <div class="mt-2">
          <data-input
            dense
            :data-type="{ name: 'text' }"
            v-model="filters.search_filter"
            prepend-inner-icon="mdi-magnify"
            :placeholder="$i18n.t('default.find')"
            @keyup.enter="handleFilter(false)"
            class="searchInput"
            height="20"
            clearable
          />
        </div>
      </v-col>
      <v-col cols="auto">
        <dots-menu
          font-size="19px"
          :items="tableMenuItems"
          close-on-content-click
          @downloadData="openDownloadAttributesDialog"
          @resetColumnState="resetColumnStateHandler"
          @uploadData="openUploadAttributesDialog"
        >
        </dots-menu>
      </v-col>
    </template>
    <template #body>
      <ag-grid-vue
        v-show="isTableVisible"
        style="width: 100%; height: 100%;"
        class="ag-theme-material"
        :gridOptions="gridOptions"
        :rowHeight="tableRowHeightUserSetting"
        :headerHeight="tableHeaderHeight"
        :style="highlightedRowColors"
        :context="context"
        :overlayNoRowsTemplate="overlayNoRowsTemplate"
      >
      </ag-grid-vue>
      <main-dialog
        v-if="isResetColumnStateDialogLoaded && isResetColumnStateDialogVisible"
        :is-visible.sync="isResetColumnStateDialogVisible"
        :title="$i18n.t('dialog.title.resetColumnState')"
        :body="$i18n.t('dialog.body.resetColumnState')"
        agree-text="menu.reset"
        @agree="resetColumnState"
      >
      </main-dialog>
      <main-dialog
        v-if="isRestoreFeatureDialogLoaded"
        :is-visible.sync="isRestoreFeatureDialogOpen"
        :title="$i18n.t('dialog.title.restoreFeature')"
        :body="
          $i18n.t('dialog.body.restoreFeatureConfirmation', {
            history_id: currentLayerSelectedFeatures ? currentLayerSelectedFeatures[0] : null,
          })
        "
        @agree="restoreFeature"
        :loading="isRestoreFeatureDialogLoading"
        agree-text="dialog.agreeRestore"
      />
      <main-large-dialog
        ref="filterBuilderDialog"
        v-if="isFilterBuilderLoaded"
        :is-visible.sync="isFilterBuilderVisible"
        :title="$i18n.t('dialog.title.addAdvancedFilter')"
        min-body-height="55vh"
        dialog-width="60%"
        header-shadow
        agree-text="dialog.agreeAdd"
        :is-valid="isFilterBuilderValid"
        @agree="handleFilter"
        @modalClosed="handleCloseExpressionBuilder"
      >
        <template #body>
          <v-col cols="12">
            <expression-builder
              class="expressions"
              ref="builderFilter"
              @update:isValid="isFilterBuilderValid = $event"
              v-model="filters.builder_filter"
              :is-data-source-selectable="false"
              :main-data-source="currentLayerDataSourceName"
              :custom-data-source-attributes="expressionBuilderAttributes"
            />
          </v-col>
        </template>
      </main-large-dialog>
      <attachments-dialog
        v-if="isAttachmentDialogLoaded"
        :is-visible.sync="isAttachmentVisible"
        :features="selectedFeaturesForAttachments"
      ></attachments-dialog>
      <quick-komadres-order
        v-if="isKomadresDialogLoaded"
        :is-visible.sync="isKomadresDialogVisible"
        :layer-id="layerId"
        :selected-features="currentLayerSelectedFeatures"
      />
      <send-message-dialog
        v-if="isSendMessageDialogLoaded"
        :is-visible.sync="isSendMessageDialogVisible"
        :selected-features="currentLayerSelectedFeatures"
        :configuration-list="sendMessageLayers"
        :is-send-email-sms-configuration-enabled="isSendEmailSmsConfigurationEnabled"
      />
      <sql-query-dialog
        v-if="isSqlQueryDialogLoaded"
        :is-visible.sync="isSqlQueryDialogVisible"
        :main-layer-id="Number(layerId)"
        :project-layers="projectLayers"
        :layers="layers"
        :layers-metadata="metadata"
      />
      <send-billings-message-dialog
        v-if="isSendBillingMessageDialogLoaded"
        :is-visible.sync="isSendBillingMessageDialogVisible"
        :selected-features="currentLayerSelectedFeatures"
      />
      <billing-water-usage-analysis-dialog
        v-if="isBillingWaterUsageAnalysisLoaded"
        :is-visible.sync="isBillingWaterUsageAnalysisVisible"
        :selected-items="currentLayerSelectedFeatures"
      />
      <relation-assignment-dialog
        v-if="isAssignRelationLoaded"
        :is-visible.sync="isAssignRelationVisible"
        :selected-features="currentLayerSelectedFeatures"
      ></relation-assignment-dialog>
      <download-attributes-dialog
        v-if="isDownloadAttributesDialogLoaded"
        :is-visible.sync="isDownloadAttributesDialogVisible"
        :layer-id="layerId"
        :items="currentLayerForm"
        :has-geometry="hasGeometry"
      />
      <upload-attributes-menu v-model="isUploadAttributesDialogVisible" />
      <masterplan-streets-situation-copy-dialog
        v-if="isMasterplanStreetsSituationCopyLoaded"
        :is-visible.sync="isMasterplanStreetsSituationCopyVisible"
        :selected-items="currentLayerSelectedFeatures"
      />
      <add-edit-feature-dialog
        v-if="isOrdersLoaded"
        :is-visible.sync="isOrdersVisible"
        v-bind="ordersDialog"
        @done="onOrdersDone"
      />
      <attribute-stats-dialog
        v-if="attributeStatsLoaded"
        :is-visible.sync="attributeStatsVisible"
        v-bind="attributeStatsDialog"
      />
      <slot name="dialogs"> </slot>
    </template>
  </bottom-drawer-wrapper>
</template>

<script>
import { debounce, isEqual } from 'lodash';
import { AgGridVue } from 'ag-grid-vue';
import { get, sync, call } from 'vuex-pathify';

import cells from '@/mixins/cells';
import filters from '@/mixins/filters';
import selectionTable from '@/mixins/selectionTable';
import selectedFeatures from '@/mixins/selectedFeatures';
import fetchAttributesSchemaMixin from '@/mixins/fetchAttributesSchemaMixin';
import prepareAttributesTableMixin from '@/mixins/prepareAttributesTableMixin';
import * as tableButtonsMixins from '@/mixins/tableButtons';

import ExpressionBuilder from '@/components/ExpressionBuilder';
import DotsMenu from '@/components/DotsMenu';
import TableButtons from '@/components/TableButtons';
import TableButtonsEdit from '@/components/TableButtonsEdit';
import DataInput from '@/components/DataInput';
import BottomDrawerWrapper from './BottomDrawerWrapper';
import UploadAttributesMenu from '@/components/UploadAttributesMenu';

export default {
  name: 'Table',
  mixins: [
    cells,
    filters,
    selectionTable,
    selectedFeatures,
    prepareAttributesTableMixin,
    fetchAttributesSchemaMixin,
    ...Object.values(tableButtonsMixins),
  ],
  components: {
    DataInput,
    AgGridVue,
    ExpressionBuilder,
    MainLargeDialog: () => import('@/components/MainLargeDialog'),
    DotsMenu,
    AttachmentsDialog: () => import('@/components/AttachmentsDialog'),
    TableButtons,
    TableButtonsEdit,
    BottomDrawerWrapper,
    UploadAttributesMenu,
    MainDialog: () => import('@/components/MainDialog'),
    QuickKomadresOrder: () => import('@/components/QuickKomadresOrder'),
    SendMessageDialog: () => import('@/components/SendMessageDialog'),
    SqlQueryDialog: () => import('@/components/SqlQueryDialog'),
    SendBillingsMessageDialog: () => import('@/components/SendBillingsMessageDialog'),
    BillingWaterUsageAnalysisDialog: () => import('@/components/BillingWaterUsageAnalysisDialog'),
    DownloadAttributesDialog: () => import('@/components/DownloadAttributesDialog'),
    MasterplanStreetsSituationCopyDialog: () => import('@/components/MasterplanStreetsSituationCopyDialog'),
    RelationAssignmentDialog: () => import('@/components/RelationAssignmentDialog'),
    AddEditFeatureDialog: () => import('@/components/AddEditFeatureDialog'),
    AttributeStatsDialog: () => import('@/components/AttributeStatsDialog'),
  },
  props: {
    statusesItems: {
      type: Array,
      required: false,
    },
    layersFilters: {
      type: Object,
      default: () => {},
    },
    itemsPerPage: {
      type: Number,
      default: 100,
    },
    isCustomLayer: {
      type: Boolean,
      default: false,
    },
    customLayerData: {
      type: Object,
      default: () => {},
    },
    getFeaturesFunctionName: {
      type: String,
      default: 'getLayerFeatures',
    },
    hasEditButtons: {
      type: Boolean,
      default: false,
    },
    initSorting: {
      type: Array,
    },
    hasGeometry: {
      type: Boolean,
      default: true,
    },
  },
  data: vm => ({
    context: {},
    noRowsText: vm.$i18n.t('map.loadingTableData'),
    expressionBuilderAttributes: [],
    unchangedLayers: undefined,
    tableHeaderHeight: undefined,
  }),
  computed: {
    currentTableSort: sync('table/currentTableSort'),
    mapPanelDataFetched: get('map/mapPanelDataFetched'),
    mapRelationDataFetched: get('map/mapRelationDataFetched'),
    tableHeight: sync('table/tableHeight'),
    tableRowHeightUserSetting: get('users/currentUser@settings.attribute_table_row_height'),
    isTableVisible() {
      return this.tableHeight > 0;
    },
    activeTool: sync('tools/activeTool'),
    isActiveToolNotReplaceable: get('tools/isActiveToolNotReplaceable'),
    isCopyButtonActive: get('edit/isCopyButtonActive'),
    isModifySnappingActive: get('edit/isModifySnappingActive'),
    isActiveModuleIdentification() {
      return this.activeTool === 'moduleIdentification';
    },

    layers: get('layers/layers'),
    metadata: get('layers/metadata'),
    permissions: get('users/layersPermissions'),
    hasEditPermissions() {
      return this.permissions[this.layerId]?.main_value === 2;
    },

    currentLayer: get('layers/currentLayer'),
    currentLayerName: get('layers/currentLayer@name'),
    currentLayerDataSourceName: sync('layers/currentLayer@data_source_name'),
    currentLayerDataSource: get('layers/metadata@:currentLayerDataSourceName'),
    isUseQualifiedNames: get('layers/metadata@:currentLayerDataSourceName.attributes_use_datasource_qualified_names'),
    currentLayerGeometryType() {
      return this.currentLayer.geometry_type;
    },
    currentLayerScope() {
      return this.currentLayer.layer_scope;
    },
    isCurrentLayerPointGeometry() {
      return this.currentLayerGeometryType === 'point';
    },

    idPropertyName: get('layers/metadata@:currentLayerDataSourceName.attributes_schema.id_name'),

    projectElements: get('layers/project@layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
    tableTabs: get('table/tabs'),

    layerId() {
      return this.isCustomLayer ? this.customLayerData.layerId : Number(this.$route.params.lid);
    },
    isCacheLayer() {
      return (this.projectLayers || []).find(layer => layer.id == this.layerId)?.cache || false;
    },
    isCutoffLayer() {
      return this.$route.name === 'cutoffLayerTable';
    },

    vehiclesInterval: get('admin/settingsValues@vehicles_module_locations_refresh_interval'),
    vehiclesLayerId: get('admin/modulesMapping@vehicles.layer_id'),
    isVehiclesLayer() {
      return this.layerId == this.vehiclesLayerId;
    },
    isTasksVisible() {
      return !this.isVehiclesLayer;
    },

    polesLayerGeomId: get('admin/modulesMapping@zdm_data.layers.pole'),
    pickArmsLayerGeomId: get('admin/modulesMapping@zdm_data.layers.pick_arm'),
    lightHeadsLayerGeomId: get('admin/modulesMapping@zdm_data.layers.head'),
    lampSubfeaturesLayersIds() {
      return [this.polesLayerGeomId, this.pickArmsLayerGeomId, this.lightHeadsLayerGeomId];
    },

    zdmStreetLampDataSourceName: get('admin/modulesMapping@zdm_data.datasources.street_lamp.datasource'),
    cableDataSourceName: get('admin/modulesMapping@zdm_data.datasources.cable.datasource'),
    isCableOrLampDatasource() {
      return (
        this.currentLayerDataSourceName === this.cableDataSourceName ||
        this.currentLayerDataSourceName === this.zdmStreetLampDataSourceName
      );
    },

    zdmReadonlyDatasources: get('admin/modulesMapping@zdm_data.read_only_datasources'),
    isZdmGeomLayers() {
      return (this.zdmReadonlyDatasources || []).includes(this.currentLayerDataSourceName);
    },
    isSqlSimpleLocal() {
      return this.currentLayerDataSource?.datasource_type === 'sql_simple_local';
    },
    isLimitedEditionDataSource() {
      return ['sql_simple_local', 'external_db_simple_local'].includes(this.currentLayerDataSource?.datasource_type);
    },

    isEditButtonsVisible() {
      return (
        this.hasEditPermissions &&
        this.hasEditButtons &&
        !this.isVehiclesLayer &&
        !this.isZdmGeomLayers &&
        !this.unchangedLayers &&
        !this.isCacheLayer &&
        !this.isLimitedEditionDataSource
      );
    },

    gridOptions() {
      return {
        rowData: null,
        columnDefs: null,
        pagination: false,
        rowSelection: false,
        onRowClicked: this.onRowClicked,
        onRowDoubleClicked: this.onRowDoubleClicked,
        onSelectionChanged: this.onSelectionChanged,
        onGridReady: this.onGridReady,
        suppressDragLeaveHidesColumns: true,
        onColumnMoved: e => {
          this.saveColumnState(e.columnApi.getColumnState());
        },
        onColumnResized: e => {
          this.saveColumnState(e.columnApi.getColumnState());
        },
        getRowId: data => {
          return data?.data?.[this.idPropertyName];
        },
        rowClassRules: {
          'ag-row-highlighted': params => {
            return (this.currentLayerHighlitedFeatures || []).includes(+params?.node?.id);
          },
        },
        localeText: {
          loadingOoo: this.$i18n.t('map.loadingTableData'),
        },
        defaultColDef: this.defaultColDef,
        components: {
          StatusCell: this.getStatusCell(),
          ColorSelectCell: this.getColorSelectCell(),
          agColumnHeader: this.getColumnHeader(),
        },
        rowModelType: 'infinite',
        paginationPageSize: this.itemsPerPage,
        cacheBlockSize: this.itemsPerPage,
        maxBlocksInCache: 10,
        cacheOverflowSize: 2,
      };
    },
    highlightedRowColors() {
      return {
        '--border-color': this.$_colors.error,
        '--background-color': `${this.$_colors.error}1f`,
      };
    },
    overlayNoRowsTemplate() {
      return `<span class="ag-overlay-loading-center">${this.noRowsText}</span>`;
    },
    loadedItemsText() {
      return `(${this.filteredItems}/${this.totalItems})`;
    },
    titleTrigger() {
      return `${this.currentLayerName}|${this.loadedItemsText}`;
    },
    buttons() {
      return [
        ...(!this.unchangedLayers
          ? [
              this.toggleFilterHeadersButton,
              this.toggleFilterBuilderButton,
              this.toggleFilterClearButton,
              this.selectionButton,
              this.clearSelectionButton,
              this.toggleFilterSelectedButton,
              ...this.circularSectorSelectionButton,
              ...this.antennaSignalDeterminationTool,
              this.selectAllRowsButton,
              ...this.restoreHistoryFeatureButton,
              ...this.zoomToSelectedButton,
              ...this.openAttachmentsDialogButton,
              this.relationAssignmentButton,
              ...this.komadresButton,
              ...this.sendMessageButton,
              ...this.bufferButton,
              ...this.sendBillingMessageButton,
              ...this.billingWaterUsageAnalysisButton,
              ...this.realtimeStationsMeasurementsButton,
              ...this.realtimeStationsDirectionsButton,
              ...this.masterplanStreetsButton,
              ...this.ordersButton,
            ]
          : []),
      ];
    },
  },

  watch: {
    titleTrigger: {
      immediate: true,
      handler() {
        this.$store.set('table/SET_TABLE_PROPERTIES!', {
          name: this.currentLayerName,
          appendName: this.loadedItemsText,
        });
      },
    },
    currentLayerSelectedFeatures(nV, oV) {
      this.highlightSelectedRows(nV);
      if (this.isFilterSelectedActive) {
        if (isEqual(oV, nV)) {
          return;
        } else if (!oV || !nV) {
          this.toggleFilterSelectedAction();
          return;
        }
        this.handleFilter();
      }
    },
    currentLayerHighlitedFeatures(nV, oV) {
      this.gridOptions.api.redrawRows({ rowNodes: this.getRowsToUpdate(nV || oV) });
    },
    tableRowHeightUserSetting() {
      this.$store.set('snackbar/PUSH_MESSAGE!', {
        message: this.$i18n.t('snackbar.attributesTableRefreshRequired'),
        customActionButton: {
          title: this.$i18n.t('button.refresh'),
          listeners: {
            click: () => {
              this.$root.$emit('redrawTableComponent');
            },
          },
        },
      });
    },
    mapPanelDataFetched: {
      immediate: true,
      handler(nV) {
        if (nV && this.unchangedLayers) {
          this.onLayerChanged(this.unchangedLayers.oV, this.unchangedLayers.nV);
        }
      },
    },
    mapRelationDataFetched: {
      immediate: true,
      handler(nV) {
        if (nV && this.unchangedLayers) {
          this.onLayerChanged(this.unchangedLayers.oV, this.unchangedLayers.nV);
        }
      },
    },
  },
  methods: {
    getLayerFeatures: call('layers/getLayerFeatures'),
    getLayerFeature: call('layers/getLayerFeature'),
    saveColumnState(columns) {
      const currentState = JSON.parse(localStorage.getItem('columnsState')) || {};
      const columnsState = columns.reduce((total, { colId, width }, index) => {
        return {
          ...total,
          [colId]: {
            width,
            index,
          },
        };
      }, {});
      localStorage.setItem('columnsState', JSON.stringify({ ...currentState, [this.layerId]: columnsState }));
    },
    getZdmLampParentFeatureId(properties) {
      const key = Object.keys(properties).find(key => /street_lamp_id/.test(key));
      return properties[key];
    },
    onRowClicked(e) {
      if (this.isSelectionActive || !e || !e.node || (!+e.node.id && +e.node.id !== 0)) {
        if (this.isSelectionActive && this.isHistoryLayer)
          this.selectedHistoryFeatureData = {
            type: e.data.fc_type,
            layerId: e.data.fl_id,
          };
        return;
      }
      if (this.lampSubfeaturesLayersIds.includes(+this.layerId)) {
        this.$router.push({
          name: 'layerFeature',
          params: {
            lid: this.layerId,
            fid: +e.node?.id,
          },
          query: {
            lamp_id: this.getZdmLampParentFeatureId(e.data),
            id: +e.node?.id,
            type: this.currentLayerDataSourceName.startsWith('zdm_data')
              ? this.currentLayerDataSourceName.split('zdm_data_')[1]
              : null,
          },
        });
      } else {
        this.$router.push({
          name: this.isCustomLayer ? this.customLayerData.routeName : 'layerFeature',
          params: {
            lid: this.isCustomLayer && this.customLayerData.routeName !== 'layerFeature' ? '' : this.layerId,
            fid: +e.node?.id,
          },
        });
      }
    },
    async onRowDoubleClicked(e) {
      if (this.hasGeometry) {
        const { crs, geometry } = await this.getLayerFeature({ layer_id: this.layerId, feature_id: e.node.id });
        this.$root.$emit('createGeomAndFitView', { ...geometry, crs });
      }
    },
    getRowsToUpdate(ids = this.currentLayerHighlitedFeatures) {
      const { api } = this.gridOptions;
      return (ids || [])
        .map(id => {
          return api.getRowNode(id + '');
        })
        .filter(row => row); // highlited rows may not be currently rendered in table
    },
    onBodyScroll() {
      this.highlightSelectedRows(this.currentLayerSelectedFeatures);
      this.gridOptions.api.redrawRows({ rowNodes: this.getRowsToUpdate() });
    },
    clearFiltersOnLayerChanged() {
      if (this.isFilterBuilderValid) {
        this.$refs.builderFilter.clearAllFilteres();
      }
    },
    setFiltersOnLayerChanged() {
      this.filters =
        this.layersFilters[this.layerId] && this.layersFilters[this.layerId].filters
          ? JSON.parse(JSON.stringify(this.layersFilters[this.layerId].filters))
          : {
              builder_filter: undefined,
              headers_filter: {},
              search_filter: '',
              navbar_filter: {},
              sidebar_filter: {},
            };
      if (!this.filters.builder_filter) {
        this.clearFiltersOnLayerChanged();
      }
    },
    onGridReady() {
      // Workaround for closing the table from another component
      if (!this.gridOptions || !this.gridOptions.api) {
        return;
      }
      this.gridOptions.api.addEventListener('bodyScroll', this.onBodyScroll);
    },
    async onLayerChanged(oV, nV) {
      const withUnchangedLayers = JSON.parse(JSON.stringify(this.unchangedLayers ? true : false));
      if (oV?.id || oV?.id === 0) {
        this.$store.set('map/DELETE_SELECTED_FEATURES!', {
          type: 'selectedFeatures',
          features: {
            [oV.id]: [],
          },
        });
      }
      this.$refs?.editButtons?.tableLayerChanged(oV, nV);
      this.setFiltersOnLayerChanged();
      if (this.gridOptions && this.gridOptions.api) {
        this.gridOptions.api.showLoadingOverlay();
      }
      this.toogleOffSelection();
      if (this.isAnyFeatureSelected) {
        this.clearSelectionAction();
      }
      if (this.mapPanelDataFetched) {
        const relations = this.getRelationalValues(
          this.currentLayerForm,
          this.metadata[this.currentLayerDataSourceName].attributes_schema.attributes
        );
        if (relations.length > 0 && !this.mapRelationDataFetched) {
          this.unchangedLayers = { oV, nV };
          return;
        } else {
          this.unchangedLayers = undefined;
        }
      } else {
        this.unchangedLayers = { oV, nV };
        return;
      }
      // Workaround for closing the table from another component
      if (!this.currentLayer) {
        return;
      }
      if (withUnchangedLayers) {
        this.$root.$emit('tableDataFetched');
      }
      this.setColumnDefs();
      this.expressionBuilderAttributes = this.getExpressionBuilderAttributes(
        this.currentLayer.form_schema.elements,
        this.attributesMetadata
      );
      this.initTableDatasource();
      if (!this.floatingFilterOpened) {
        this.toggleFilterHeadersAction();
      }
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
      if (this.isCustomLayer) {
        this.$root.$emit(this.customLayerData.eventName, true);
      }
      this.isHeadersFilterActive = this.getHeadersFilterActive();
      this.isBuilderFilterActive = this.getBuilderFilterExpression();
      this.isSearchFilterActive = this.getSearchFilterActive();
      this.isFilterActive = this.getFilterActive();
      if (this.floatingFilterOpened) {
        this.toggleFilterHeadersAction();
      }
      if (this.initSorting) {
        const sortModel = this.initSorting.map(element => {
          const { attribute, sort } = element;
          return { colId: attribute, sort };
        });
        this.gridOptions.api.setSortModel(sortModel);
      }
      if (oV && this.layersFilters[oV?.id]) {
        this.$root.$emit('filterProjectLayer', oV.id, this.layersFilters[oV.id].filterExpression);
      }
    },
    showNoRowsOverlay(text) {
      this.noRowsText = text;
      setTimeout(() => {
        this.gridOptions.api.showNoRowsOverlay();
      }, 0);
    },
    async fetchRows(params, page, sorting, features_filter) {
      const currentSortState = params.sortModel.map(column => ({
        attribute_name: column.colId,
        direction: column.sort,
      }));
      this.currentTableSort = currentSortState;
      this.gridOptions.api.refreshHeader();
      return await this[this.getFeaturesFunctionName]({
        layer_id: this.layerId,
        sorting: sorting || currentSortState,
        features_filter,
        params: {
          limit: this.itemsPerPage,
          with_total_count: true,
          with_count: true,
          with_geometry: false,
          with_total_count_without_filter: true,
          page,
        },
      });
    },
    initTableDatasource({ sorting, clearSelection = true, features_filter = this.handleGetFilter() } = {}) {
      const self = this;
      this.totalItems = 0;
      this.filteredItems = 0;
      const dataSource = {
        gisboxcontext_currentDataLayerId: self.layerId,
        rowCount: null, // behave as infinite scroll
        async getRows(params) {
          /** Checking if current AG Grid datasource context layer refers to an actual current layer  */
          if (self.layerId !== this.gisboxcontext_currentDataLayerId) return;
          const page = params.startRow / self.itemsPerPage;
          // page is equal to 0 on table init, layer change, after filtering or sorting and after refreshing (even when table were scrolled to the bottom)
          if (page === 0) {
            self.gridOptions.api.showLoadingOverlay();
            if (!self.isRefreshing && !self.isCopyButtonActive && clearSelection) {
              self.clearSelectionAction();
            }
          } else {
            self.showNoRowsOverlay(self.$i18n.t('map.loadingTableData'));
          }
          const r = await self.fetchRows(params, page, sorting, features_filter);
          if (self.currentLayerSelectedFeatures?.length > 0 && page === 0) {
            const rr = await self[self.getFeaturesFunctionName]({
              layer_id: self.layerId,
              features_filter,
              params: {
                ids_only: true,
                with_geometry: false,
              },
            });
            const filteredIds = rr.data.data.ids;
            const filteredSelectedIds = JSON.parse(JSON.stringify(self.currentLayerSelectedFeatures || [])).filter(id =>
              filteredIds.includes(id)
            );
            self.currentLayerSelectedFeatures = filteredSelectedIds;
          }
          self.gridOptions.api.hideOverlay();
          const { total_features_count_without_filter, total_features_count } = r.data.data;
          // layer is empty or filters returned no results
          if (total_features_count === 0) {
            self.showNoRowsOverlay(self.$i18n.t('map.noData'));
          }
          self.filteredItems = total_features_count;
          self.totalItems = total_features_count_without_filter;
          params.successCallback(
            r.data.data.features.features.map(feature => ({
              ...feature.properties,
              [self.idPropertyName]: self.$_hasAnyValue(feature.id) ? feature.id : feature.properties.id,
            })),
            r.data.data.total_features_count
          );
          self.isRefreshing = false;
          self.$root.$emit('tableDataRowsFetched');
        },
      };
      this.gridOptions.api.setDatasource(dataSource);
    },
    sortColumns(columnState, mappedElements) {
      return columnState
        ? mappedElements.sort((a, b) => {
            if (!columnState[a.field]) {
              return 1;
            }
            if (!columnState[b.field]) {
              return -1;
            }
            return columnState[a.field]?.index - columnState[b.field]?.index;
          })
        : mappedElements;
    },
    getColumnState() {
      const columnsState = JSON.parse(localStorage.getItem('columnsState'));
      return columnsState?.[this.layerId];
    },
    setColumnDefs(defs = this.getSortedColumDefs()) {
      /*
        this.gridOptions.api.setColumnDefs([]) - resets columns state and properties with same name eg. id will not cause
        issues while sorting (repetetive names are suffixed with _1)
        https://github.com/ag-grid/ag-grid/issues/2889#issuecomment-555816473
      */
      this.gridOptions.api.setColumnDefs([]);
      this.gridOptions.api.setColumnDefs(defs);
    },
    refreshTableData() {
      this.isRefreshing = true;
      // refreshCache() don't trigger getRows in aggrid model when there are no rows in grid
      // because there's initially no data in cache and therefore no rows to refresh,
      // row model needs to be purged instead
      // https://www.ag-grid.com/javascript-data-grid/infinite-scrolling/#reference-infiniteScrolling-purgeInfiniteCache
      this.gridOptions.api.infiniteRowModel.getRowCount() <= 0
        ? this.gridOptions.api.infiniteRowModel.purgeCache()
        : this.gridOptions.api.infiniteRowModel.refreshCache();
    },
    // TODO - 🤮 do reaktoryzacji
    refreshVehicles() {
      this.vehiclesTimeout = setTimeout(() => {
        this.refreshVehicles();
      }, (this.vehiclesInterval || 5) * 1000);
    },
    onProjectFetch() {
      if (this.$route.params.lid) {
        this.toogleOffSelection();
        if (this.isAnyFeatureSelected) {
          this.clearSelectionAction();
        }
        this.$off();
      }
    },
    beforeDestroy() {
      this.currentTableSort = null;
      this.toogleOffSelection(false);
      if (this.isAnyFeatureSelected) {
        this.clearSelectionAction();
      }
      this.$off();
    },
    getTableButtonsListeners() {
      const listeners = {};
      this.buttons.forEach(button => {
        if (!button.disableAction && this[`${button.name}Action`]) {
          listeners[button.name] = this[`${button.name}Action`];
        }
      });
      return listeners;
    },
  },
  // https://www.ag-grid.com/vue-grid/component-communication/
  beforeMount() {
    this.tableHeaderHeight = this.tableRowHeightUserSetting;
    this.context = {
      contextParent: this,
    };
  },
  mounted() {
    this.$watch(
      'currentLayer',
      (nV, oV) => {
        if (!nV) {
          return;
        }
        if (this.tableHeaderHeight !== this.tableRowHeightUserSetting)
          this.tableHeaderHeight = this.tableRowHeightUserSetting;
        this.onLayerChanged(oV, nV);
      },
      {
        immediate: true,
      }
    );
    this.onBodyScroll = debounce(this.onBodyScroll, 150);
    this.$root.$off('setAdditionalFilters');
    this.$root.$off('setSidebarFilters');
    this.$root.$off('refreshTableData');
    this.$root.$on('setAdditionalFilters', this.setAdditionalFilters);
    this.$root.$on('setSidebarFilters', this.setSidebarFilters);
    this.$root.$on('refreshTableData', this.refreshTableData);
    this.$root.$on('fetchProject', this.onProjectFetch);
    this.$once('hook:beforeDestroy', this.beforeDestroy);
    if (this.isVehiclesLayer) {
      this.refreshVehicles();
    }
  },
};
</script>

<style lang="scss" scoped>
@import '../../node_modules/ag-grid-community/styles/ag-grid.css';
@import '../../node_modules/ag-grid-community/styles/ag-theme-material.css';

.background {
  @include activeToolButtonStyle();
}

.selection--active {
  width: 27px !important;
  svg {
    width: 26px;
  }
}

.selection-badge :deep(.v-badge__wrapper) {
  z-index: 1;
}

.selection-badge :deep(.v-badge__badge) {
  pointer-events: none;
}

::v-deep {
  .searchInput {
    .v-input__slot {
      min-height: 0px !important;
      padding: 0 4px !important;
    }
    .v-input__append-inner {
      margin: 0 !important;
    }
    .v-input__prepend-inner {
      margin: 0 !important;
      .v-input__icon {
        .v-icon {
          font-size: 16px;
          padding-bottom: 3px;
        }
      }
    }
    .v-label {
      font-size: 12px;
      top: 0 !important;
    }
    .v-text-field__slot input {
      font-size: 12px !important;
      padding-bottom: 3px !important;
    }
  }
  .v-input__icon .v-icon {
    font-size: 18px;
  }
  .expressions .v-text-field__slot input {
    font-size: 14px;
  }
  .v-text-field__slot input {
    font-size: 12px;
  }
  .ag-header-cell-text {
    text-align: center;
    width: 100%;
  }
  .ag-overlay-no-rows-center {
    padding-top: 20px;
  }
  .ag-cell-focus {
    border: none !important;
  }
  .ag-center-cols-container {
    min-width: 100%;
  }
  .ag-row-selected {
    background-color: rgba(250, 255, 0, 0.22);
  }
  .ag-row {
    display: flex;
    align-items: center;
  }
  .ag-cell {
    line-height: unset !important;
    height: auto;
  }
  .ag-cell div {
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .ag-row-highlighted {
    border: 2px solid var(--border-color);
  }
  .ag-row-highlighted:not(.ag-row-selected) {
    background-color: var(--background-color);
  }
  .ag-row-selected::before {
    background: unset !important;
  }
  .ag-header-cell-label:hover {
    .header-information {
      .v-icon {
        display: block !important;
        cursor: pointer;
      }
    }
  }
}
</style>
